import { Bell, Shield, Globe, Moon, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@packages/store/hooks";
import { setLanguage } from "@packages/store/userSlice";
import { Button } from "@core/button";
import Select, { StylesConfig } from "react-select";

interface SelectOption {
  value: string;
  label: string;
}

interface SettingSectionProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

const SettingSection = ({
  title,
  description,
  children,
}: SettingSectionProps) => (
  <div className="border-b border-gray-200 py-6">
    <div className="mb-4">
      <h3 className="text-lg font-medium text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500">{description}</p>
    </div>
    {children}
  </div>
);

const ProfilesView = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const currentLanguage = useAppSelector(
    (state) => state.user.teacher?.preferences?.language,
  );

  const handleLanguageChange = (option: SelectOption | null) => {
    if (option) {
      dispatch(setLanguage(option.value));
      i18n.changeLanguage(option.value);
    }
  };

  // Language options for react-select
  const languageOptions: SelectOption[] = [
    { value: "en", label: t("settings.language.english") },
    { value: "tr", label: t("settings.language.turkish") },
  ];

  // react-select styles with proper typing
  const selectStyles: StylesConfig<SelectOption, false> = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base) => ({
      ...base,
      backgroundColor: "var(--background, #fff)",
      color: "var(--foreground, #1a202c)",
      borderColor: "var(--border, #d1d5db)",
      borderRadius: "0.375rem",
      minHeight: "2rem",
      fontSize: "0.875rem",
      lineHeight: "1.25rem",
      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
      transition: "all 0.2s",
      "&:hover": {
        borderColor: "var(--border-hover, #9ca3af)",
      },
      "&:focus-within": {
        borderColor: "var(--primary-500, #3b82f6)",
        boxShadow: "0 0 0 1px var(--primary-500, #3b82f6)",
      },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "var(--primary-500, #3b82f6)"
        : state.isFocused
          ? "var(--primary-100, #dbeafe)"
          : "var(--background, #fff)",
      color: state.isSelected ? "#fff" : "var(--foreground, #1a202c)",
      fontSize: "0.875rem",
      padding: "0.5rem 1rem",
      "&:hover": {
        backgroundColor: state.isSelected
          ? "var(--primary-500, #3b82f6)"
          : "var(--primary-100, #dbeafe)",
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "var(--background, #fff)",
      borderRadius: "0.375rem",
      border: "1px solid var(--border, #d1d5db)",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--foreground, #1a202c)",
      fontSize: "0.875rem",
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--gray-400, #9ca3af)",
      fontSize: "0.875rem",
    }),
  };

  return (
    <div className="flex-1 p-8 bg-gray-50">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-800">
            {t("settings.title")}
          </h1>
          <p className="text-gray-600 mt-1">{t("settings.description")}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6">
            {/* Profile Section */}
            <SettingSection
              title={t("settings.profile.title")}
              description={t("settings.profile.description")}
            >
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t("settings.profile.name")}
                  </label>
                  <input
                    type="text"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    defaultValue="Sarah Johnson"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t("settings.profile.email")}
                  </label>
                  <input
                    type="email"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    defaultValue="sarah.johnson@school.edu"
                  />
                </div>
              </div>
            </SettingSection>

            {/* Notifications */}
            <SettingSection
              title={t("settings.notifications.title")}
              description={t("settings.notifications.description")}
            >
              <div className="space-y-4">
                {[
                  {
                    icon: Bell,
                    title: t("settings.notifications.assignmentUpdates"),
                    description: t(
                      "settings.notifications.assignmentUpdatesDesc",
                    ),
                  },
                  {
                    icon: Mail,
                    title: t("settings.notifications.emailNotifications"),
                    description: t(
                      "settings.notifications.emailNotificationsDesc",
                    ),
                  },
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                        defaultChecked
                      />
                    </div>
                    <div className="ml-3">
                      <label className="font-medium text-gray-700">
                        {item.title}
                      </label>
                      <p className="text-gray-500 text-sm">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </SettingSection>

            {/* Language */}
            <SettingSection
              title={t("settings.language.title")}
              description={t("settings.language.description")}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Globe className="h-5 w-5 text-gray-400 mr-3" />
                  <span className="text-gray-700">
                    {t("settings.language.label")}
                  </span>
                </div>
                <div className="w-40">
                  <Select<SelectOption, false>
                    options={languageOptions}
                    value={
                      languageOptions.find(
                        (option) => option.value === currentLanguage,
                      ) || null
                    }
                    onChange={handleLanguageChange}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                    menuPosition="absolute"
                    menuPlacement="auto"
                    menuShouldScrollIntoView={false}
                  />
                </div>
              </div>
            </SettingSection>

            {/* Preferences */}
            <SettingSection
              title={t("settings.preferences.title")}
              description={t("settings.preferences.description")}
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Moon className="h-5 w-5 text-gray-400 mr-3" />
                    <span className="text-gray-700">
                      {t("settings.preferences.theme")}
                    </span>
                  </div>
                  <span className="text-gray-500 text-sm">
                    {t("settings.preferences.light")}
                  </span>
                </div>
              </div>
            </SettingSection>

            {/* Security */}
            <SettingSection
              title={t("settings.security.title")}
              description={t("settings.security.description")}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Shield className="h-5 w-5 text-gray-400 mr-3" />
                  <span className="text-gray-700">
                    {t("settings.security.twoFactor")}
                  </span>
                </div>
                <Button
                  variant="solid"
                  bgColor="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                  textColor="text-gray-800 dark:text-gray-100"
                  size="sm"
                  rounded="lg"
                >
                  {t("settings.security.enable")}
                </Button>
              </div>
            </SettingSection>
          </div>

          <div className="bg-gray-50 px-6 py-4 rounded-b-lg">
            <div className="flex justify-end space-x-3">
              <Button
                variant="solid"
                bgColor="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                textColor="text-gray-800 dark:text-gray-100"
                size="md"
                rounded="lg"
              >
                {t("settings.cancel")}
              </Button>
              <Button
                bgColor="bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700"
                textColor="white"
                size="md"
                rounded="lg"
              >
                {t("settings.save")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilesView;
